import * as React from 'react'
import { Alert, Box, Button, Divider, Grid, Paper, Typography } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { ScaffoldRequestForm } from '../../../types/Scaffold/ScaffoldRequest'
import { formatLocaleShortDate } from '../../../services/CommonServices/DataFormatters'
import { showErrorToast, showWarningToast } from '../../../services/CommonServices/alertServices'
import logoHeader from '../../../assets/images/logo-header.png'
import { downloadScaffoldReceiptPdf, isPdfFile } from '../../../utils/scaffoldReceiptPdf'
import ReceiptAttachments from './ReceiptAttachments'

type RequestReceiptProps = {
	receipt: ScaffoldRequestForm
	files: File[]
}

type KeyInfoItemProps = {
	label: string
	value: React.ReactNode
	color?: string
}

function KeyInfoItem({ label, value, color = 'primary.main' }: KeyInfoItemProps) {
	return (
		<Grid item xs={12} sm={6} md={3}>
			<Typography variant='subtitle2' color='primary.dark' sx={{ fontWeight: 'bold' }}>
				{label}
			</Typography>
			<Typography variant='h6' sx={{ fontWeight: 'bold', color }}>
				{value}
			</Typography>
		</Grid>
	)
}

const priorityColor = (priority?: string | null) => {
	if (priority === 'Urgent') return 'error.main'
	if (priority === 'High') return 'warning.main'
	return 'primary.main'
}

export default function RequestReceipt({ receipt, files }: RequestReceiptProps) {
	const receiptRef = React.useRef<HTMLDivElement>(null)
	const [generatingReceipt, setGeneratingReceipt] = React.useState(false)

	const imageFiles = React.useMemo(() => files.filter((file) => !isPdfFile(file)), [files])
	const pdfFiles = React.useMemo(() => files.filter(isPdfFile), [files])
	const imageUrls = React.useMemo(
		() => imageFiles.map((file) => URL.createObjectURL(file)),
		[imageFiles],
	)

	React.useEffect(() => {
		return () => imageUrls.forEach((url) => URL.revokeObjectURL(url))
	}, [imageUrls])

	const generatePDF = async () => {
		if (!receiptRef.current) return

		setGeneratingReceipt(true)
		try {
			const { skippedPdfNames } = await downloadScaffoldReceiptPdf(
				receiptRef.current,
				receipt.id,
				imageFiles,
				pdfFiles,
			)
			if (skippedPdfNames.length > 0) {
				showWarningToast(
					`These PDFs could not be added to the receipt: ${skippedPdfNames.join(', ')}`,
				)
			}
		} catch (error) {
			console.error('Error generating PDF:', error)
			showErrorToast({ message: 'Failed to generate PDF. Please try again.' })
		} finally {
			setGeneratingReceipt(false)
		}
	}

	return (
		<Alert
			severity='success'
			sx={{
				mt: 3,
				borderRadius: 2,
				'& .MuiAlert-message': {
					width: '100%',
				},
			}}
		>
			<Box sx={{ width: '100%' }}>
				<Typography variant='h6' sx={{ fontWeight: 'bold', mb: 2, color: 'success.dark' }}>
					Request Submitted Successfully!
				</Typography>

				<Paper
					ref={receiptRef}
					data-ref='receipt'
					elevation={2}
					sx={{
						p: 3,
						mt: 2,
						bgcolor: 'white',
						border: '1px solid',
						borderColor: 'success.light',
						borderRadius: 2,
					}}
				>
					<Box sx={{ display: 'flex', mb: 3 }}>
						<img
							src={logoHeader}
							alt='Company Logo'
							style={{
								maxHeight: '60px',
								maxWidth: '200px',
								objectFit: 'contain',
							}}
						/>
					</Box>
					<Divider sx={{ my: 3 }} />
					<Typography variant='h6' sx={{ fontWeight: 'bold', mb: 3, color: 'primary.main' }}>
						Request Receipt
					</Typography>

					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Box
								sx={{
									p: 2,
									bgcolor: 'primary.light',
									borderRadius: 1,
									mb: 2,
									border: '1px solid',
									borderColor: 'primary.main',
								}}
							>
								<Grid container spacing={2}>
									<KeyInfoItem label='Request ID' value={`#${receipt.id}`} />
									<KeyInfoItem label='Action' value={receipt.action} />
									{receipt.tagNumber && (
										<KeyInfoItem label='Tag Number' value={`#${receipt.tagNumber}`} />
									)}
									<KeyInfoItem
										label='Priority'
										value={receipt.priority}
										color={priorityColor(receipt.priority)}
									/>
								</Grid>
							</Box>
						</Grid>

						<Grid item xs={12} md={6}>
							<Typography
								variant='subtitle1'
								sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}
							>
								👤 Requester Information
							</Typography>
							<Box sx={{ pl: 2 }}>
								<Typography variant='body2' sx={{ mb: 0.5 }}>
									<strong>Contractor:</strong> {receipt.contractorName}
								</Typography>
								<Typography variant='body2' sx={{ mb: 0.5 }}>
									<strong>Requestor:</strong> {receipt.requestorName}
								</Typography>
								{receipt.requestorEmail && (
									<Typography variant='body2' sx={{ mb: 0.5 }}>
										<strong>Email:</strong> {receipt.requestorEmail}
									</Typography>
								)}
								<Typography variant='body2' sx={{ mb: 0.5 }}>
									<strong>Site Contact:</strong> {receipt.siteContact}
								</Typography>
								<Typography variant='body2'>
									<strong>Site Contact Email:</strong> {receipt.siteContactEmail}
								</Typography>
							</Box>
						</Grid>

						<Grid item xs={12} md={6}>
							<Typography
								variant='subtitle1'
								sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}
							>
								🔧 Work Details
							</Typography>
							<Box sx={{ pl: 2 }}>
								<Typography variant='body2' sx={{ mb: 0.5 }}>
									<strong>Discipline:</strong> {receipt.discipline}
								</Typography>
								<Typography variant='body2' sx={{ mb: 0.5 }}>
									<strong>PO Number:</strong> {receipt.po}
								</Typography>
								<Typography variant='body2' sx={{ mb: 0.5 }}>
									<strong>Work Order:</strong> {receipt.workOrder}
								</Typography>
								<Typography variant='body2'>
									<strong>Required Date:</strong>{' '}
									{receipt.requiredDate ? formatLocaleShortDate(receipt.requiredDate) : 'N/A'}
								</Typography>
							</Box>
						</Grid>

						<Grid item xs={12}>
							<Typography
								variant='subtitle1'
								sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}
							>
								📍 Work Location
							</Typography>
							<Box sx={{ pl: 2 }}>
								<Typography variant='body2' sx={{ mb: 0.5 }}>
									<strong>Area:</strong> {receipt.area}
								</Typography>
								{receipt.exactLocation && (
									<Typography variant='body2'>
										<strong>Exact Location:</strong> {receipt.exactLocation}
									</Typography>
								)}
							</Box>
						</Grid>

						{receipt.notes && (
							<Grid item xs={12}>
								<Typography
									variant='subtitle1'
									sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}
								>
									📝 Notes
								</Typography>
								<Box sx={{ pl: 2 }}>
									<Typography
										variant='body2'
										sx={{
											fontStyle: 'italic',
											bgcolor: 'grey.100',
											p: 1,
											borderRadius: 1,
											borderLeft: '3px solid',
											borderLeftColor: 'primary.main',
										}}
									>
										{receipt.notes}
									</Typography>
								</Box>
							</Grid>
						)}

						{files.length > 0 && <ReceiptAttachments files={files} imageUrls={imageUrls} />}

						<Grid item xs={12}>
							<Divider sx={{ my: 2 }} />
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
								}}
							>
								<Typography variant='caption' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
									Submitted on {new Date().toLocaleString()}
								</Typography>
								<Button
									variant='contained'
									color='primary'
									startIcon={<DownloadIcon />}
									onClick={generatePDF}
									disabled={generatingReceipt}
									sx={{
										px: 3,
										py: 1,
										fontSize: '0.875rem',
										fontWeight: 'bold',
										borderRadius: 2,
										textTransform: 'none',
										'&:hover': {
											boxShadow: 4,
											transform: 'translateY(-1px)',
										},
										transition: 'all 0.2s ease-in-out',
									}}
								>
									Download Receipt
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Paper>
			</Box>
		</Alert>
	)
}
